// This laptop's player: the code, its two-word name and the slime. The laptop remembers it so the code is
// rarely typed; the game server keeps the slime so a new laptop can bring it back with the code.
import { cleanLook, cleanOwned, type SlimeLook, type Tier } from '../slime/look';
import { cleanFurniture, cleanLayout, ownedFurniture, type RoomLayout } from '../slime/room';
import { gameServer } from './client';

export interface PlayerProfile {
  code: string; // "Brave Otter 4821"
  name: string; // "Brave Otter"
  look: SlimeLook;
  logins?: string[]; // sign-ins that also open this slime
  owned?: string[]; // earned parts, e.g. "colour:rust"
  konpeito?: number;
  tokens?: number; // capsule machine turns
  medals?: number; // the cellar's game medals (cranes and 一番くじ only)
  hopBest?: number; // best Slime Hop score the game server has checked
  dashBest?: { best: number; week: string } | null; // best Milky Way Dash play on this week's course (week: Tokyo Monday)
  renamesLeft?: number; // name changes left today (three a day; the code never changes)
  recent?: WalletLine[]; // the last few changes to konpeito and capsules, newest first
  tips?: string[]; // Koma's first-time hints this slime has seen
  room?: RoomLayout | null; // the slime's own room, as its student last arranged it (null: as every room starts)
  furniture?: string[]; // pieces for the room earned on top of the starter ones
  parcels?: { id: string; item: string; at: number }[]; // bundles the Inari's foxes brought to the room, not opened yet
  sticker?: string | null; // the purikura sticker it printed last (its name on the game server); none: the starter one
  prints?: PrintSheet[]; // whole sheets from the booth, oldest first, for the プリ帳 and the room's walls
}

// One print from the booth: the sheet and its four cuts, each by its name on the game server (stickers/<name>)
export interface PrintSheet {
  id: string;
  cuts: string[];
  at: number;
}
const NAME = /^[0-9a-f]{24}$/;
const cleanPrints = (v: unknown): PrintSheet[] =>
  Array.isArray(v)
    ? v
        .filter((x) => x && typeof x.id === 'string' && NAME.test(x.id) && Array.isArray(x.cuts) && typeof x.at === 'number')
        .map((x) => ({ id: x.id, cuts: (x.cuts as unknown[]).filter((c): c is string => typeof c === 'string' && NAME.test(c)), at: x.at }))
    : [];

export interface WalletLine {
  at: number;
  kind: 'game' | 'capsule' | 'swap' | 'hop' | 'dash' | 'solo' | 'gift' | 'other';
  konpeito?: number;
  tokens?: number;
  item?: string;
}

function profile(p: PlayerProfile): PlayerProfile {
  const owned = cleanOwned(p.owned);
  const prints = cleanPrints(p.prints);
  return {
    code: p.code,
    name: p.name,
    look: cleanLook(p.look, owned),
    logins: p.logins ?? [],
    owned,
    konpeito: Math.max(0, Number(p.konpeito) || 0),
    tokens: Math.max(0, Number(p.tokens) || 0),
    medals: Math.max(0, Number(p.medals) || 0),
    hopBest: Math.max(0, Number(p.hopBest) || 0),
    dashBest: p.dashBest && typeof p.dashBest.week === 'string' ? { best: Math.max(0, Number(p.dashBest.best) || 0), week: p.dashBest.week } : null,
    renamesLeft: Math.max(0, Number(p.renamesLeft ?? 3) || 0),
    recent: Array.isArray(p.recent) ? p.recent.slice(0, 8) : [],
    tips: Array.isArray(p.tips) ? p.tips.filter((t) => typeof t === 'string') : [],
    room: p.room ? cleanLayout(p.room, ownedFurniture(p.furniture, prints.length), ['small'], prints.map((x) => x.id)) : null,
    furniture: cleanFurniture(p.furniture),
    parcels: Array.isArray(p.parcels) ? p.parcels.filter((x) => typeof x?.id === 'string' && typeof x.item === 'string' && typeof x.at === 'number') : [],
    sticker: typeof p.sticker === 'string' && NAME.test(p.sticker) ? p.sticker : null,
    prints,
  };
}

const KEY = 'gk-player';

export function loadPlayer(): PlayerProfile | null {
  try {
    const p = JSON.parse(localStorage.getItem(KEY) || 'null');
    return p && typeof p.code === 'string' && typeof p.name === 'string' ? profile(p) : null;
  } catch {
    return null;
  }
}

export function savePlayer(p: PlayerProfile | null) {
  try {
    if (p) localStorage.setItem(KEY, JSON.stringify(p));
    else localStorage.removeItem(KEY);
  } catch {
    /* private window: the code has to be typed next time */
  }
  // the wallet plate in the corner listens for this, so konpeito and capsules tick as they change
  window.dispatchEvent(new Event(PLAYER_EVENT));
}

export const PLAYER_EVENT = 'gk-player';

export type PlayerResult = { ok: true; player: PlayerProfile } | { ok: false; error: 'unknown' | 'wait' | 'pass' | 'unreachable' | 'renamed' };

async function call(pass: string, path: string, init: RequestInit = {}): Promise<PlayerResult> {
  const base = await gameServer();
  if (!base) return { ok: false, error: 'unreachable' };
  try {
    const r = await fetch(`${base}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${pass}` },
    });
    if (r.status === 404) return { ok: false, error: 'unknown' };
    if (r.status === 429) return { ok: false, error: 'wait' };
    if (r.status === 401) return { ok: false, error: 'pass' };
    if (r.status === 409) return { ok: false, error: 'renamed' };
    if (!r.ok) return { ok: false, error: 'unreachable' };
    const p = (await r.json()) as PlayerProfile;
    return { ok: true, player: profile(p) };
  } catch {
    return { ok: false, error: 'unreachable' };
  }
}

export const createPlayer = (pass: string, name: string, look: SlimeLook) =>
  call(pass, '/players', { method: 'POST', body: JSON.stringify({ name, look }) });

export const fetchPlayer = (pass: string, code: string) => call(pass, `/players/${encodeURIComponent(code.trim())}`);

export const updatePlayer = (pass: string, code: string, look: SlimeLook, name?: string) =>
  call(pass, `/players/${encodeURIComponent(code)}`, { method: 'PUT', body: JSON.stringify({ look, name }) });

// The slime's room as its student rearranged it; the server keeps only what its catalog allows, and answers with it
export const saveRoom = (pass: string, code: string, room: RoomLayout) =>
  call(pass, `/players/${encodeURIComponent(code)}/room`, { method: 'PUT', body: JSON.stringify({ room }) });

// My slime opened a bundle the Inari's foxes brought: what was in it is my slime's now (the answer leaves the bundle out)
export const openParcel = (pass: string, code: string, parcel: string) =>
  call(pass, `/players/${encodeURIComponent(code)}/open`, { method: 'POST', body: JSON.stringify({ parcel }) });

// The purikura sticker my slime just printed at the booth (a small WebP as a data URL); the answer carries its name
export const saveSticker = (pass: string, code: string, image: string) =>
  call(pass, `/players/${encodeURIComponent(code)}/sticker`, { method: 'PUT', body: JSON.stringify({ image }) });
// A whole print for the slime's プリ帳: the sheet and its four cuts as data URLs; `pick` is the cut that is its sticker
export const savePrint = (pass: string, code: string, sheet: string, cuts: string[], pick: number) =>
  call(pass, `/players/${encodeURIComponent(code)}/prints`, { method: 'POST', body: JSON.stringify({ sheet, cuts, pick }) });
// A cut from one of its saved prints as its sticker again
export const pickSticker = (pass: string, code: string, id: string) =>
  call(pass, `/players/${encodeURIComponent(code)}/sticker`, { method: 'PUT', body: JSON.stringify({ id }) });

// Koma's hints this slime has now seen; the server adds them to the ones it had.
export const sendTips = (pass: string, code: string, tips: string[]) =>
  call(pass, `/players/${encodeURIComponent(code)}/tips`, { method: 'POST', body: JSON.stringify({ tips }) });

// ---------- signing in with Microsoft, Google or LINE ----------

export const PROVIDER_NAMES: Record<string, string> = {
  microsoft: 'Microsoft',
  google: 'Google',
  line: 'LINE',
  test: 'Test',
};

export async function loginProviders(): Promise<string[]> {
  const base = await gameServer();
  if (!base) return [];
  try {
    const r = await fetch(`${base}/auth/providers`);
    return r.ok ? ((await r.json()).providers as string[]) : [];
  } catch {
    return [];
  }
}

const SECRET_KEY = 'gk-login-secret';

// Leaves the site for the provider's sign-in page. With `link`, the login is added to that slime; without it,
// the name and look make a new slime unless this account already has one.
export async function startLogin(
  pass: string,
  provider: string,
  opts: { link?: string; name?: string; look?: SlimeLook },
): Promise<'unreachable' | 'pass' | null> {
  const base = await gameServer();
  if (!base) return 'unreachable';
  try {
    const r = await fetch(`${base}/auth/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${pass}` },
      body: JSON.stringify({ provider, return: `${location.origin}/play/slime`, ...opts }),
    });
    if (r.status === 401) return 'pass';
    if (!r.ok) return 'unreachable';
    const { url, secret } = await r.json();
    sessionStorage.setItem(SECRET_KEY, secret);
    location.assign(url);
    return null;
  } catch {
    return 'unreachable';
  }
}

// Back from the provider with #login=<ticket>: swap the ticket for the slime this login opens.
export async function finishLogin(ticket: string): Promise<{ player: PlayerProfile; existing: boolean; already: boolean } | null> {
  const base = await gameServer();
  let secret = '';
  try {
    secret = sessionStorage.getItem(SECRET_KEY) || '';
    sessionStorage.removeItem(SECRET_KEY);
  } catch {
    /* private window */
  }
  if (!base || !secret || !ticket || ticket === 'failed') return null;
  try {
    const r = await fetch(`${base}/auth/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket, secret }),
    });
    if (!r.ok) return null;
    const { player, existing, already } = await r.json();
    return {
      player: profile(player),
      existing: !!existing,
      already: !!already,
    };
  } catch {
    return null;
  }
}

// The capsule machine. The server draws the capsule; the laptop only shows it.
export type PullResult =
  | { ok: true; item: string; tier: Tier; duplicate: boolean; player: PlayerProfile }
  | { ok: false; error: 'empty' | 'unknown' | 'wait' | 'pass' | 'unreachable' };

async function machine(pass: string, code: string, action: 'pull' | 'swap', room?: string, which?: number): Promise<PullResult | PlayerResult> {
  const base = await gameServer();
  if (!base) return { ok: false, error: 'unreachable' };
  try {
    const r = await fetch(`${base}/players/${encodeURIComponent(code)}/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${pass}` },
      body: room ? JSON.stringify({ room, machine: which }) : undefined,
    });
    if (r.status === 409) return { ok: false, error: 'empty' } as PullResult;
    if (r.status === 404) return { ok: false, error: 'unknown' };
    if (r.status === 429) return { ok: false, error: 'wait' };
    if (r.status === 401) return { ok: false, error: 'pass' };
    if (!r.ok) return { ok: false, error: 'unreachable' };
    const d = await r.json();
    if (action === 'swap') return { ok: true, player: profile(d) };
    return { ok: true, item: String(d.item), tier: d.tier, duplicate: !!d.duplicate, player: profile(d.player) };
  } catch {
    return { ok: false, error: 'unreachable' };
  }
}

// `room`: the class game this tab is sitting in, so the gachapon in that room's lobby turns for everyone to see;
// `which`: which of the yard's three gachapons (0 to 2) the student is turning
export const pullCapsule = (pass: string, code: string, room?: string, which?: number) => machine(pass, code, 'pull', room, which) as Promise<PullResult>;
export const swapKonpeito = (pass: string, code: string) => machine(pass, code, 'swap') as Promise<PlayerResult | PullResult>;
